/* eslint-disable no-console, @stylistic/max-len */
/**
 * CRM Storage Service - TelegramAI CRM
 * Administra el estado de postulantes, embudo de conversión, programas y textos rápidos.
 */

import { CRM_BACKEND_URL as BACKEND_API_URL } from '../util/crmBackendUrl';

export interface LeadRecord {
  chatId: string;
  fullName: string;
  username?: string;
  phone?: string;
  ci?: string;
  funnelStage: string;
  assignedOperator?: string;
  notes?: string;
  isChannelSubscribed?: boolean;
  keywords?: string[];
  updatedAt: string;
}

export const FUNNEL_STAGES = [
  '🔵 1. Interesado',
  '🟡 2. Requisitos',
  '🟣 3. Pre-inscrito',
  '🟢 4. Matriculado',
  '⚪ 5. Descartado',
] as const;

export interface QuickReplyItem {
  id: string;
  label: string;
  text: string;
}

export const DEFAULT_QUICK_REPLIES: QuickReplyItem[] = [
  {
    id: 'saludo_canal',
    label: '👋 Saludo + Canal',
    text: `Estimado/a profesional, le saluda la Secretaría de Posgrado de la Facultad de Medicina UMSS (EGEMC). Es un placer orientarle.

📢 Para no perderse ninguna convocatoria oficial, ampliación de plazos ni fechas de inicio de nuestros programas, le recomendamos unirse a nuestro Canal Oficial de Telegram:
👉 https://t.me/PosgradoMedicinaUMSS

¿En qué programa académico o trámite podemos colaborarle hoy?`,
  },
  {
    id: 'despedida_canal',
    label: '🤝 Despedida + Canal',
    text: `Ha sido un gusto atenderle. La Secretaría de Posgrado de la Facultad de Medicina UMSS queda a su entera disposición ante cualquier duda futura.

📌 Le recordamos unirse a nuestro Canal Oficial para enterarse de las próximas aperturas y calendarios académicos:
👉 https://t.me/PosgradoMedicinaUMSS

¡Le deseamos mucho éxito en su desarrollo profesional!`,
  },
  {
    id: 'requisitos_admision',
    label: '📋 Requisitos de Admisión',
    text: `📌 Requisitos generales de admisión a Posgrado EGEMC:
1. Fotocopia legalizada del Título en Provisión Nacional (o Título Académico según aplique).
2. Fotocopia simple de Cédula de Identidad.
3. Formulario oficial de preinscripción debidamente llenado.
4. Comprobante de depósito bancario.

Puede consultar los requisitos específicos en nuestro portal: https://posgrado.med.umss.edu.bo`,
  },
  {
    id: 'formas_pago',
    label: '💳 Formas de Pago',
    text: `💰 Modalidades de Pago Posgrado Medicina UMSS:
- Al Contado: 10% de descuento sobre el arancel total del programa.
- Plan en Cuotas: Cuotas mensuales modulares sin interés durante el desarrollo académico.

Los depósitos se realizan en la cuenta oficial fiscal del Banco Unión a nombre de la Facultad de Medicina UMSS.`,
  },
];

export const QUICK_REPLIES = DEFAULT_QUICK_REPLIES;

const QUICK_REPLIES_STORAGE_KEY = 'telegram_crm_quick_replies_v1';

export function getStoredQuickReplies(): QuickReplyItem[] {
  try {
    const raw = localStorage.getItem(QUICK_REPLIES_STORAGE_KEY);
    if (!raw) return [...DEFAULT_QUICK_REPLIES];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
    return [...DEFAULT_QUICK_REPLIES];
  } catch {
    return [...DEFAULT_QUICK_REPLIES];
  }
}

export function saveStoredQuickReplies(replies: QuickReplyItem[]): void {
  try {
    localStorage.setItem(QUICK_REPLIES_STORAGE_KEY, JSON.stringify(replies));
    window.dispatchEvent(new CustomEvent('crm:quick-replies-changed', { detail: replies }));
  } catch (err) {
    console.warn('[CRM] Error al guardar textos rápidos:', err);
  }
}

export function resetStoredQuickReplies(): QuickReplyItem[] {
  try {
    localStorage.removeItem(QUICK_REPLIES_STORAGE_KEY);
    const defaults = [...DEFAULT_QUICK_REPLIES];
    window.dispatchEvent(new CustomEvent('crm:quick-replies-changed', { detail: defaults }));
    return defaults;
  } catch {
    return [...DEFAULT_QUICK_REPLIES];
  }
}

export interface Operator {
  id: string;
  name: string;
  role: string;
  pin?: string; // SHA-256 hex del PIN de 6 dígitos — nunca texto plano
}

/** Hashea un PIN en texto plano usando SHA-256 (Web Crypto API nativa). */
export async function hashPin(rawPin: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(rawPin);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Compara un PIN ingresado contra el hash almacenado */
export async function verifyPin(rawPin: string, storedHash: string): Promise<boolean> {
  const computed = await hashPin(rawPin);
  return computed === storedHash;
}

export const OPERATORS: Operator[] = [
  { id: 'op_luis', name: 'Luis Villegas', role: 'SuperUsuario' },
  { id: 'op_juan', name: 'Juan', role: 'Operador Admisiones' },
  { id: 'op_maria', name: 'María', role: 'Coordinación Académica' },
  { id: 'op_carla', name: 'Carla', role: 'Operadora Admisiones' },
  { id: 'op_stefan', name: 'Stefan', role: 'Operador Soporte' },
  { id: 'op_nilce', name: 'Nilce', role: 'Operadora Admisiones' },
  { id: 'op_adm7', name: 'Admisiones Turno 7', role: 'Operador Turno' },
  { id: 'op_adm8', name: 'Admisiones Turno 8', role: 'Operador Turno' },
];

export const OPERATORS_STORAGE_KEY = 'telegram_crm_operators_v1';
const OPERATOR_KEY = 'telegram_crm_active_operator_id';

export function isSuperUser(operator?: Operator): boolean {
  if (!operator) return false;
  if (operator.id === 'op_luis') return true;
  const roleLower = (operator.role || '').toLowerCase();
  return roleLower.includes('super') || roleLower.includes('superusuario');
}

export function getActiveOperator(): Operator {
  try {
    const savedId = localStorage.getItem(OPERATOR_KEY);
    let operators = OPERATORS;
    const stored = localStorage.getItem(OPERATORS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Operator[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        operators = parsed;
      }
    }
    const found = operators.find((op) => op.id === savedId) || OPERATORS.find((op) => op.id === savedId);
    return found || operators[0] || OPERATORS[0];
  } catch {
    return OPERATORS[0];
  }
}

export function setActiveOperator(operatorId: string): void {
  try {
    localStorage.setItem(OPERATOR_KEY, operatorId);
    window.dispatchEvent(new CustomEvent('crm:operator-changed', { detail: operatorId }));
  } catch (err) {
    console.warn('[CRM] Error guardando operador activo:', err);
  }
}

export function getStoredOperators(): Operator[] {
  try {
    const stored = localStorage.getItem(OPERATORS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Operator[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // Fallback
  }
  return OPERATORS;
}

export async function fetchOperatorsApi(): Promise<Operator[]> {
  try {
    const res = await fetch(`${BACKEND_API_URL}/operators`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        localStorage.setItem(OPERATORS_STORAGE_KEY, JSON.stringify(data));
        window.dispatchEvent(new CustomEvent('crm:operators-changed', { detail: data }));
        return data;
      }
    }
  } catch (err) {
    console.warn('[CRM] Error cargando operadores desde MariaDB:', err);
  }
  return getStoredOperators();
}

export async function createOperatorApi(payload: { name: string; role: string; pin?: string }): Promise<Operator | undefined> {
  try {
    const res = await fetch(`${BACKEND_API_URL}/operators`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const data = await res.json();
      await fetchOperatorsApi();
      return data.operator;
    }
  } catch (err) {
    console.error('[CRM] Error creando operador en MariaDB:', err);
  }
  return undefined;
}

export async function updateOperatorApi(id: string, payload: { name?: string; role?: string; pin?: string }): Promise<Operator | undefined> {
  try {
    const res = await fetch(`${BACKEND_API_URL}/operators/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const data = await res.json();
      await fetchOperatorsApi();
      return data.operator;
    }
  } catch (err) {
    console.error('[CRM] Error actualizando operador en MariaDB:', err);
  }
  return undefined;
}

export async function deleteOperatorApi(id: string): Promise<boolean> {
  try {
    const res = await fetch(`${BACKEND_API_URL}/operators/${id}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      await fetchOperatorsApi();
      return true;
    }
  } catch (err) {
    console.error('[CRM] Error eliminando operador en MariaDB:', err);
  }
  return false;
}

export async function verifyOperatorPinApi(id: string, pin: string): Promise<boolean> {
  try {
    const res = await fetch(`${BACKEND_API_URL}/operators/${id}/verify-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    if (res.ok) {
      const data = await res.json();
      return Boolean(data.success && data.match);
    }
  } catch (err) {
    console.error('[CRM] Error verificando PIN con MariaDB:', err);
  }
  return false;
}

const STORAGE_KEY = 'telegram_crm_leads_v1';

export function getStoredLeads(): Record<string, LeadRecord> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const leads = JSON.parse(raw) as Record<string, LeadRecord>;
    let hasChanged = false;
    for (const key of Object.keys(leads)) {
      if ('programCode' in (leads[key] as any)) {
        delete (leads[key] as any).programCode;
        hasChanged = true;
      }
    }
    if (hasChanged) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));
    }
    return leads;
  } catch {
    return {};
  }
}

export function getLead(chatId: string, defaultName = 'Postulante'): LeadRecord {
  const leads = getStoredLeads();
  if (leads[chatId]) {
    return leads[chatId];
  }

  const activeOp = getActiveOperator();

  // Si no existe, crear registro neutral sin forzar programa académico
  const newLead: LeadRecord = {
    chatId,
    fullName: defaultName,
    funnelStage: '🔵 1. Interesado',
    assignedOperator: activeOp.name,
    keywords: [],
    updatedAt: new Date().toISOString(),
  };

  saveLead(newLead);
  return newLead;
}

export function saveLead(lead: LeadRecord): void {
  try {
    const leads = getStoredLeads();
    leads[lead.chatId] = {
      ...lead,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));

    // Notificar cambio de estado local
    window.dispatchEvent(new CustomEvent('crm:lead-updated', { detail: lead }));

    // Sincronizar centralmente con backend Laravel / MariaDB para los 8 operadores
    syncLeadToBackend(lead);
  } catch (err) {
    console.warn('[CRM] Error al guardar lead:', err);
  }
}

export interface CrmSlaConfig {
  warningMinutes: number;
  urgentMinutes: number;
  sortBySla: boolean;
}

export const DEFAULT_SLA_CONFIG: CrmSlaConfig = {
  warningMinutes: 30,
  urgentMinutes: 45,
  sortBySla: true,
};

const SLA_CONFIG_STORAGE_KEY = 'telegram_crm_sla_config_v1';

export function getStoredSlaConfig(): CrmSlaConfig {
  try {
    const raw = localStorage.getItem(SLA_CONFIG_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SLA_CONFIG };
    const parsed = JSON.parse(raw);
    return {
      warningMinutes: typeof parsed.warningMinutes === 'number' ? parsed.warningMinutes : DEFAULT_SLA_CONFIG.warningMinutes,
      urgentMinutes: typeof parsed.urgentMinutes === 'number' ? parsed.urgentMinutes : DEFAULT_SLA_CONFIG.urgentMinutes,
      sortBySla: typeof parsed.sortBySla === 'boolean' ? parsed.sortBySla : DEFAULT_SLA_CONFIG.sortBySla,
    };
  } catch {
    return { ...DEFAULT_SLA_CONFIG };
  }
}

export function saveStoredSlaConfig(config: CrmSlaConfig): void {
  try {
    localStorage.setItem(SLA_CONFIG_STORAGE_KEY, JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('crm:sla-config-changed', { detail: config }));
  } catch (err) {
    console.warn('[CRM] Error al guardar configuración SLA:', err);
  }
}

export async function fetchLeadApi(chatId: string): Promise<LeadRecord | undefined> {
  try {
    const res = await fetch(`${BACKEND_API_URL}/conversations/${chatId}/lead`);
    if (res.ok) {
      const data = await res.json();
      if (data.found && data.lead) {
        const lead = data.lead as LeadRecord;
        if ('programCode' in (lead as any)) {
          delete (lead as any).programCode;
        }
        const leads = getStoredLeads();
        leads[chatId] = lead;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));
        window.dispatchEvent(new CustomEvent('crm:lead-updated', { detail: lead }));
        return lead;
      }
    }
  } catch (err) {
    console.warn('[CRM] Error cargando lead desde MariaDB:', err);
  }
  return undefined;
}

async function syncLeadToBackend(lead: LeadRecord): Promise<void> {
  try {
    await fetch(`${BACKEND_API_URL}/conversations/${lead.chatId}/sync-lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: lead.chatId,
        full_name: lead.fullName,
        phone: lead.phone,
        ci: lead.ci,
        funnel_stage: lead.funnelStage,
        assigned_operator: lead.assignedOperator,
        notes: lead.notes,
        is_channel_subscribed: lead.isChannelSubscribed,
        keywords: lead.keywords || [],
      }),
    }).catch(() => {
      // Backend offline o en espera de endpoint de persistencia
    });
  } catch {
    // Ignorar en fallback local
  }
}
